import type { Edge, Node } from '@xyflow/react';
import { useCallback, useEffect, useState } from 'react';
import type { AutomationStep, StepConnection } from '../types/automation';
import type { ValidationResult } from '../utils/flowValidation';
import { validateFlow } from '../utils/flowValidation';

/**
 * Runs flow validation whenever the steps or connections change, returning
 * the current `ValidationResult`.
 *
 * Extracted from `ListFlowBuilder` (Phase 0 of the builder 2.0 work) so the
 * 1.0 and 2.0 builders validate identically. `validateFlow` itself already
 * lives in `utils/flowValidation.ts`; this hook just owns the React-Flow
 * shape conversion + the effect wiring.
 */
export function useFlowValidation(
	steps: Array<AutomationStep>,
	connections: Array<StepConnection>,
): ValidationResult {
	const [validationResult, setValidationResult] = useState<ValidationResult>(
		new Map(),
	);

	// Convert steps and connections to React Flow format for validation.
	const convertToNodesAndEdges = useCallback((): {
		nodes: Node[];
		edges: Edge[];
	} => {
		const nodes: Node[] = steps.map((step) => ({
			id: step.internalId ?? step.id?.toString() ?? '', // Use internalId (UUID) for variable references
			type: step.stepType,
			position: { x: 0, y: 0 },
			data: {
				label: step.stepName,
				stepType: step.stepType,
				config: step.config,
				isConfigured:
					step.config != null && Object.keys(step.config).length > 0,
			},
		}));

		// Create a map from step.id to step.internalId for edge conversion
		const stepIdToInternalId = new Map<number, string>();
		steps.forEach((step) => {
			if (step.id != null && step.internalId != null) {
				stepIdToInternalId.set(step.id, step.internalId);
			}
		});

		const edges: Edge[] = connections.map((conn, index) => ({
			id: `e${index}`,
			source:
				stepIdToInternalId.get(conn.fromStepId) ?? conn.fromStepId.toString(),
			target:
				conn.toStepId != null
					? (stepIdToInternalId.get(conn.toStepId) ?? conn.toStepId.toString())
					: '',
			sourceHandle: conn.conditionKey,
			targetHandle: null,
		}));

		return { nodes, edges };
	}, [steps, connections]);

	// Run validation when steps or connections change.
	useEffect(() => {
		const { nodes, edges } = convertToNodesAndEdges();
		const result = validateFlow(nodes, edges);
		setValidationResult(result);
	}, [steps, connections, convertToNodesAndEdges]);

	return validationResult;
}
